import type { BenchmarkScoring } from '@vizit/api';
import { type PickerProductCategory, toDistinctProductCategoryNames } from '@vizit/organisms';
import { toPickerCategory } from './use-benchmark-scorings';

/** What naming a scoring needs: its category, and the name the listing gave it, if any. */
type NamedScoring = Pick<BenchmarkScoring, 'productCategoryId' | 'productCategoryName'>;

/** Finds a category by ID, as `useFindProductCategory` returns it. */
type FindProductCategory = (categoryId: string) => PickerProductCategory | undefined;

/**
 * A page's scorings as the category picker lists them, and each one's name as the copy about it writes it:
 * "Cats (Pet Supplies, Walmart)" when another of them shares its name, else "Cats". A scoring outside `scorings` is
 * named alone. Pure, so a page can memoize it on its scorings and lookup.
 */
function nameScorings(scorings: NamedScoring[], findCategory: FindProductCategory) {
  const toPicker = (scoring: NamedScoring) => toPickerCategory(scoring, findCategory(scoring.productCategoryId));
  const names = toDistinctProductCategoryNames(scorings.map(toPicker));
  const nameOf = (scoring: NamedScoring) => names.get(scoring.productCategoryId) ?? toPicker(scoring).name;
  return { toPicker, nameOf };
}

interface NameRefusedCategoriesOptions {
  /** The categories the add dialog lists */
  listed: PickerProductCategory[];
  /** The ones it leaves out, already scored or scoring */
  excludedIds: string[];
  findCategory: FindProductCategory;
}

/**
 * The names of categories the API refused to add, as the add dialog's rows show them: told apart from the other rows
 * of the same name. A pick made under an earlier search is no longer a row, and is named alone; one not found at all
 * is `undefined`, for the caller's own wording.
 */
function nameRefusedCategories(
  categoryIds: string[],
  { listed, excludedIds, findCategory }: NameRefusedCategoriesOptions,
): (string | undefined)[] {
  const excluded = new Set(excludedIds);
  const rowNames = toDistinctProductCategoryNames(listed.filter((category) => !excluded.has(category.id)));
  return categoryIds.map((id) => rowNames.get(id) ?? findCategory(id)?.name);
}

export { nameRefusedCategories, nameScorings };
